import { useId } from 'react';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react';

import { cn } from '../../lib/cn';
import { useTranslation } from '../../i18n/LocaleProvider';
import { IconButton } from '../core/IconButton';

export type InlineAlertTone = 'success' | 'warning' | 'error' | 'info';

/**
 * Each tone pairs a `*-surface` fill with its `*-on` text token, so the panel
 * keeps its measured contrast in both themes — never a hand-mixed colour.
 *
 * The urgent tones interrupt (`role="alert"`); the informational ones are a
 * named `region` the reader can find without being cut off mid-sentence.
 */
const TONES: Record<InlineAlertTone, { root: string; icon: ReactNode }> = {
  success: {
    root: 'bg-success-surface border-success text-success-on',
    icon: <IconCircleCheck />,
  },
  warning: {
    root: 'bg-warning-surface border-warning text-warning-on',
    icon: <IconAlertTriangle />,
  },
  error: { root: 'bg-danger-surface border-danger text-danger-on', icon: <IconCircleX /> },
  info: { root: 'bg-info-surface border-info text-info-on', icon: <IconInfoCircle /> },
};

export interface InlineAlertProps extends Omit<ComponentPropsWithRef<'div'>, 'title'> {
  tone?: InlineAlertTone;
  title?: string;
  description?: ReactNode;
  children?: ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  action?: ReactNode;
}

/**
 * Persistent, in-content feedback. Deliberately not a toast: it lives in the
 * flow, it has no auto-dismiss and no positioning, and it stays until the user
 * or the data removes it.
 */
export function InlineAlert({
  tone = 'info',
  title,
  description,
  children,
  dismissible = false,
  onDismiss,
  action,
  className,
  ...rest
}: InlineAlertProps) {
  const { t } = useTranslation();
  const titleId = `${useId()}-title`;
  const preset = TONES[tone];
  const isUrgent = tone === 'error' || tone === 'warning';
  // A close button is only ever rendered against a real handler.
  const canDismiss = dismissible === true && typeof onDismiss === 'function';

  return (
    <div
      {...rest}
      role={isUrgent ? 'alert' : 'region'}
      // A `region` is only exposed as a landmark when it has a name, so an
      // untitled informational alert still names itself rather than becoming an
      // anonymous landmark.
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : `${tone} message`}
      className={cn(
        'flex w-full items-start gap-3 rounded-xs border p-4',
        'animate-[dhd-fade-down_var(--dur-base)_var(--ease-out)_both]',
        preset.root,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="mt-0.5 inline-grid size-5 shrink-0 place-items-center [&>svg]:size-full"
      >
        {preset.icon}
      </span>

      <div className="flex min-w-0 flex-1 flex-col">
        {title ? (
          <p id={titleId} className="font-display text-sm font-semibold uppercase tracking-title">
            {title}
          </p>
        ) : null}
        {description ? <div className={cn('text-sm', title && 'mt-1')}>{description}</div> : null}
        {children ? <div className={cn('text-sm', (title || description) && 'mt-1')}>{children}</div> : null}
      </div>

      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}

      {canDismiss ? (
        <IconButton
          label={t('ds.alert.dismiss')}
          icon={<IconX />}
          variant="ghost"
          size="sm"
          className="shrink-0"
          onClick={onDismiss}
        />
      ) : null}
    </div>
  );
}
